import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Data Mapper ServiceStores (plan/2026-10-09_data-mapper-into-hq.md, slice 1:
 * the control plane). Mirror types live in views/dataMapper/dataMapper.types.ts.
 *
 * All four answer `find` with a BARE ARRAY scoped by the selection the caller
 * names (`?clientId` for profiles; `?clientId` / `?profileId` for schemas and
 * lookup tables — plan §2.4, the server keeps no per-tab selection). So:
 *
 * - `replaceOnFind: true` — each find is the whole set for ONE selection; a
 *   merging cache would keep the previous client's or profile's rows. The
 *   ViewStores still filter the cache by the selection, because a socket event
 *   can land a row from someone else's selection.
 * - `enableOffline: false` — the offline wrapper's sync-on-reconnect is a BARE
 *   find, which here means "every client's profiles" or "global rows only" and,
 *   with replaceOnFind, would swap the screen's rows for the wrong set.
 * - `disableInitialFind: true` — the same bare find on connect; the routes
 *   load what they show.
 * - `throwOnError: true` — one ViewStore owns and catches each store, and the
 *   API refuses viewers and API keys on every method: with the library default
 *   a refused create/patch/remove resolves null and the screen would report
 *   success.
 */
const DATA_MAPPER_STORE_OPTIONS = {
  idField: "id",
  enableStorage: false,
  enableOffline: false,
  disableInitialFind: true,
  replaceOnFind: true,
  throwOnError: true,
} as const;

export const dataMapperStores: ServiceStoreConfig[] = [
  { name: "data-mapper-profiles", options: { ...DATA_MAPPER_STORE_OPTIONS } },
  { name: "data-mapper-schemas", options: { ...DATA_MAPPER_STORE_OPTIONS } },
  { name: "data-mapper-lookup-tables", options: { ...DATA_MAPPER_STORE_OPTIONS } },
  // Saved source connections. The API strips every secret on the way in AND
  // out, and the Data Source screen sends none through this store (a REST
  // save, whose body carries the typed token and headers so the API can say
  // which it dropped, goes through the raw client instead), so caching is
  // safe here.
  { name: "data-mapper-source-configs", options: { ...DATA_MAPPER_STORE_OPTIONS } },
  // Saved TARGET connections: metadata only. The table has no password column,
  // the API drops one if sent, and the Target screen never sends one.
  { name: "data-mapper-target-connections", options: { ...DATA_MAPPER_STORE_OPTIONS } },
  // The data plane (data-mapper-data-source / -source-tables / -source-data,
  // -target-connection and -imports) is
  // deliberately NOT here: its answers are byte-paged envelopes with no ids,
  // and the connect body carries a password a ServiceStore would keep. Those
  // calls go through the raw client (dataMapperServices.ts dataPlaneService).
  // So do the explorer's (-explorer-schema / -profiler / -analysis), the admin
  // restore (its body carries a password too) and the saved explorer reports:
  // `data-mapper-explorer-reports` answers find GROUPED by database (no ids at
  // the top level) and get with the whole report, so no entity cache fits it.
  // find answers summaries, get/create/patch the whole config (with mappings);
  // a get merges over its summary in the cache (dataMapper.types.ts).
  { name: "data-mapper-mapping-configs", options: { ...DATA_MAPPER_STORE_OPTIONS } },
];
